import moment from 'moment';
import { isEmpty, isEqual, isRegExp, uniq, isNumber } from 'lodash';
import { urlParams } from './request';
import { getTenantConfig } from 'utils/tenant';
import useInterval from 'use-interval';
import useStarfishAPI from '../hooks/useStarfishAPI';

export const STEPS = {
  SIGN_IN: { key: "SIGN_IN", showHeader: false, showFooter: false, checkActivity: false, showWaitTme: false },
  AUTHENTICATED_SIGN_IN: { key: "AUTHENTICATED_SIGN_IN", showHeader: false, showFooter: false, checkActivity: false, showWaitTme: false },
  CONFIRM_APPOINTMENT: { key: "CONFIRM_APPOINTMENT", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  DISPLAY_WAIT_LIST: { key: "DISPLAY_WAIT_LIST", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true},
  CONFIRMATION_PAGE: { key: "CONFIRMATION_PAGE", showHeader: true, showFooter: false, checkActivity: false, showWaitTme: true },
  SELECT_SERVICE: { key: "SELECT_SERVICE", showHeader: false, showFooter: true, checkActivity: true, showWaitTme: false },
  SELECT_REASON: { key: "SELECT_REASON", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  PROSPECTIVE_SIGN_IN: { key: "PROSPECTIVE_SIGN_IN", showHeader: false, showFooter: true, checkActivity: false, showWaitTme: false },
  PROSPECTIVE_FORM: { key: "PROSPECTIVE_FORM", showHeader: false, showFooter: false, checkActivity: true, showWaitTme: false },
  SELECT_GROUP: { key: "SELECT_GROUP", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  SELECT_COURSE: { key: "SELECT_COURSE", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  MORE_INFORMATION: { key: "MORE_INFORMATION", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  CHECKOUT_CONFIRMATION_PAGE: { key: "CHECKOUT_CONFIRMATION_PAGE", showHeader: true, showFooter: false, checkActivity: false, showWaitTme: false },
  WAIT_LIST_CONFIRMATION_PAGE: { key: "WAIT_LIST_CONFIRMATION_PAGE", showHeader: true, showFooter: false, checkActivity: false, showWaitTme: true },
  CHECKED_IN_OPTIONS: { key: "CHECKED_IN_OPTIONS", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  NO_APPOINTMENT_SCHEDULED: { key: "NO_APPOINTMENT_SCHEDULED", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: false },
  SELECT_MEETING_TYPE_PAGE: {key: "SELECT_MEETING_TYPE_PAGE", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true },
  MULTIPLE_CHECKINS_OPTION: {key: "MULTIPLE_CHECKIN_OPTIONS", showHeader: true, showFooter: true, checkActivity: true, showWaitTme: true }

}

export const validateEmail = email => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const validateField = (field, value, comparison) => {
  switch ( field ) {
    case 'firstName':
      if ( value === '' ) return [false, "First name is required."];
      break;
    case 'lastName':
      if ( value === '' ) return [false, "Last name is required."];
      break;
    case 'email':
      if ( value === '' ) return [false, "Email is required."];
      else if ( !validateEmail(value) ) return [false, "Email is invalid."];
      else if ( value !== comparison ) {
        return [false, "The email address confirmation does not match."];
      }
      break;
    case 'emailConfirmation':
      if ( value === '' ) return [false, "Email confirmation is required."];
      else if ( !validateEmail(value) ) return [false, "Email confirmation is invalid."];
      else if ( value !== comparison ) {
        return [false, "The email address confirmation does not match."];
      }
      break;
    case 'birthday':
      if ( value === '' ) return [false, "Birthday is required."];
      else if ( !moment(value, 'MM/DD/YYYY', true).isValid() ) return [false, "Date is either invalid or not in the MM/DD/YYYY format."];
      break;
    case 'phone':
      if ( value === '' ) return [false, "Phone is required."];
      break;
    case 'zipCode':
      if ( value === '' ) return [false, "Postal code is required."];
      break;
  }
  return [true, ""];
}

export const validateProspectiveForm = (formInputs, birthdayRequired, phoneRequired, zipRequired) => {
  const { firstName, lastName, email, emailConfirmation,  phone, zipCode,  birthday } = formInputs;
  const [isFirstNameValid, firstNameErrorMessage] = validateField('firstName', firstName.value);
  const [isLastNameValid, lastNameErrorMessage] = validateField('lastName', lastName.value);
  const [isEmailValid, emailErrorMessage] = validateField('email', email.value, emailConfirmation.value);
  const [isEmailConfirmationValid, emailConfirmationErrorMessage] = validateField('emailConfirmation', emailConfirmation.value, email.value);
  const [isBirthdayValid, birthdayErrorMessage] =  birthdayRequired ? validateField('birthday', birthday.value) : [true, ''];
  const [isPhoneValid, phoneErrorMessage] =  phoneRequired ? validateField('phone', phone.value) : [true, ''];
  const [isZipCodeValid, zipCodeErrorMessage] = zipRequired ? validateField('zipCode', zipCode.value) : [true, ''];
  return {
    firstName: { value: firstName.value, isValid: isFirstNameValid, errorMessage: firstNameErrorMessage },
    lastName: { value: lastName.value, isValid: isLastNameValid, errorMessage: lastNameErrorMessage },
    email: { value: email.value, isValid: isEmailValid, errorMessage: emailErrorMessage },
    emailConfirmation: { value: emailConfirmation.value, isValid: isEmailConfirmationValid, errorMessage: emailConfirmationErrorMessage },
    birthday: { value: birthday.value, isValid: isBirthdayValid, errorMessage: birthdayErrorMessage },
    phone: { value: phone.value, isValid: isPhoneValid, errorMessage: phoneErrorMessage },
    zipCode: { value: zipCode.value, isValid: isZipCodeValid, errorMessage: zipCodeErrorMessage },
  };
};

export const isProspectiveFormValid = formInputs => {
  const { firstName, lastName, email, emailConfirmation, phone, zipCode, birthday } = formInputs;
  return firstName.isValid
    && lastName.isValid
    && email.isValid
    && emailConfirmation.isValid
    && birthday.isValid
    && phone.isValid
    && zipCode.isValid;
};

export const formatMintuesToHoursMinutesString = function (minutes) {
  let m = minutes % 60,
    h = (minutes - m) / 60;
  return `${h > 0 ? (h + 'h') : ''} ${m > 0 ? m + 'm' : ''}`;
};

export const DEFAULT_TIMEOUT = 5000;

export const getGroupSessionByReasonId = (groupSessions, reasonId) => {
  return groupSessions &&
      groupSessions.result ?
      groupSessions.result.find(gs => gs.appointmentTypeReason && gs.appointmentTypeReason.id === reasonId)
      : null;
};

export const isScheduledOnly = kioskType => kioskType === "SCHEDULED";
export const isSelfService = kioskType => kioskType === "SELF-SERVICE";
export const canViewWaitList = kioskType => !(isScheduledOnly(kioskType) || isSelfService(kioskType));

export const getMinStudentIdLength = (cardParameters) => {
  if (isEmpty(cardParameters)) return 0;
  let minimumStudentIdLength = 0;
  cardParameters.split('/').forEach(parameterSet => {
    const currentParameters = parameterSet.split(',');
    const numDigits = currentParameters.length === 4 ? Number(currentParameters[3]) : null;
    // Try the next set of params if this one doesn't have a valid number or a proper config
    if (isNumber(numDigits)) {
      minimumStudentIdLength = !minimumStudentIdLength ? numDigits : Math.min(minimumStudentIdLength, numDigits)
    }
  });
  return minimumStudentIdLength;
};

// Provided input must pass all four conditions to be considered swipe input
export const isInputFromCardSwipe = (textInput, cardParameters) => {
  // If it's empty, nothing to do regardless of the source
  if (isEmpty(textInput)) return false;
  // Verify first character is not alpha-numeric (swipe cards provide ';' or '%')
  if (textInput.charAt(0).match(/[^\w+-]+/) === null) return false;
  // Verify the input is longer than the longest expected student ID
  if (textInput.length < getMinStudentIdLength(cardParameters)) return false;
  // Final boss: Verify the last character (end sentinel) is a question mark
  return (textInput.charAt(textInput.length - 1) === '?');
};

const extractStudentId = (cardData, referenceChar, displacementCount, idLength) => {
  // These get passed in as a string, make sure they transform to valid numbers
  displacementCount = Number(displacementCount);
  idLength = Number(idLength);
  if (Number.isNaN(displacementCount) || Number.isNaN(idLength)) throw new Error("Unable to read card due to a missing or invalid value " +
    "in the card parameter configuration. Please contact your system administrator.");

  // If first character is repeated, we search for the nth instance of it
  const firstReferenceChar = referenceChar.charAt(0);
  const repeats = referenceChar.split(firstReferenceChar).length - 1;
  let recursiveIndex = cardData.indexOf(firstReferenceChar);
  for (let i = 0; i < repeats - 1; i++) {
    recursiveIndex = cardData.indexOf(firstReferenceChar, recursiveIndex + 1);
  }

  return recursiveIndex === -1 ? null : cardData.substr((recursiveIndex + displacementCount + 1), idLength);
};

export const keyboardEmulationSwipeCardHandler = (cardData, cardParameters, regexPattern, idPrefix) => {
  if ( isEmpty(cardData) ) throw new Error("Card swipe data came back empty, you may have swiped too fast for the reader to process the data. Please swipe again.");
  if ( isEmpty(cardParameters) ) throw new Error("Card swipe functionality currently unavailable, card reader parameters configuration not found. Please contact your system administrator.");
  if ( !isRegExp(regexPattern) || isEqual(regexPattern, new RegExp('')) ) throw new Error("Regular expression used for student ID matching was not found. Please contact your system administrator.");
  
  const extractedStudentIds = [];
  const cardParameterArray = cardParameters.split('/');
  
  for ( let i = 0; i < cardParameterArray.length; i++ ) {
    const currentCardParameters = cardParameterArray[i].split(',');
    if ( currentCardParameters.length !== 4 ) throw new Error("Unable to read card due to a missing value in the card parameter configuration. Please contact your system administrator.");

    const studentId = extractStudentId(cardData, currentCardParameters[1], currentCardParameters[2], currentCardParameters[3]);

    if ( !isEmpty(studentId) && studentId.match(regexPattern) ) {
      extractedStudentIds.push(idPrefix ? idPrefix + studentId : studentId);
    }
  }
  
  if (isEmpty(extractedStudentIds)) throw new Error("No valid student ID value found on card, card may either be invalid or configuration missing. Please contact your system administrator.");
  // Remove duplicates and transform array into string
  return uniq(extractedStudentIds).join(',');
};

export const isCheckedIn = status => status.userInQueue;

// For a given kiosk if the student is already checked into one of its services, return that service
export const serviceInKioskCheckedIn = (kioskId, services) =>
    services.filter(service => service.userInQueue && service.kioskId === kioskId);

export const getVirtualKioskLink = (kioskId, serviceId) => {
  return `../virtual-kiosk/launch.html?kioskId=${kioskId}&serviceId=${serviceId}`
};

/**
 * Sign student into the waiting room based on given params.
 * @param {Object} callAPI - the API calling function
 * @param {Object} params - the params needed to check the student in - different for virtual vs. in-person check-in
 */
export const signInStudent = async (callAPI, params) =>
    await callAPI('../kiosk/signInWaitingRoomStudent.html', {
      method: 'POST',
      body: urlParams(params, false)
    });

// Determines if the responsive student dashboard should be the landing page back from error page
export const showStudentDashboard = () => {
    return getTenantConfig('RESPONSIVE_STUDENT_DASHBOARD_ENABLED')
    && getTenantConfig('STUDENT_SHOW_DASHBOARD')
    && "DASHBOARD" === getTenantConfig('STUDENT_LANDING_PAGE');
};

// Student can click on the 'go back' button / 'NO' button (From MultiServiceCheckIn) that will take them back to
// either success network or responsive student dashboard depending on tenant configurations.
export const landingPageForVirtualKiosk = () => {
  return showStudentDashboard() ? "../student/dashboard.html" : "../instructor/serviceCatalog.html";
};

export const getVirtualKioskDeepLink = (uuid) => {
  return `/virtual-kiosk/signIn.html?kioskId=${uuid}`
};

// Interval at which keep alive api is called to keep the physical kiosk from timing out.
export const KEEP_ALIVE_REQUEST_INTERVAL_MINS = 31;

export const keepSessionAlive = () => {
  const [, keepAliveAPI ] = useStarfishAPI();
  useInterval(() => {
    keepAliveAPI('../appKeepAlive.html', {method: 'get'});
  }, KEEP_ALIVE_REQUEST_INTERVAL_MINS * 60 * 1000 );
}
